import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { blogAPI, contactAPI, conferencesAPI, communityAPI, testimonialsAPI, faqsAPI, mediaAPI, galleryAPI, newsletterAPI, awardsAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Globe, Heart, MessageSquare, Star, HelpCircle, Tv, Camera, Mail, Trophy } from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    blogs: 0, conferences: 0, community: 0, messages: 0, testimonials: 0,
    faqs: 0, media: 0, gallery: 0, subscribers: 0, awards: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const [blogData, confData, commData, msgData, testData, faqData, mediaData, galleryData, subData, awardsData] = await Promise.all([
        blogAPI.getPosts(1, 1).catch(() => ({ total: 0 })),
        conferencesAPI.getAll().catch(() => []),
        communityAPI.getAll().catch(() => []),
        contactAPI.getMessages().catch(() => []),
        testimonialsAPI.getTestimonials().catch(() => []),
        faqsAPI.getAll(false).catch(() => []),
        mediaAPI.getAll().catch(() => []),
        galleryAPI.getAll().catch(() => []),
        newsletterAPI.getSubscribers().catch(() => []),
        awardsAPI.getAll().catch(() => []),
      ]);
      setStats({
        blogs: blogData.total || 0,
        conferences: confData.length || 0,
        community: commData.length || 0,
        messages: msgData.length || 0,
        testimonials: testData.length || 0,
        faqs: faqData.length || 0,
        media: mediaData.length || 0,
        gallery: galleryData.length || 0,
        subscribers: subData.length || 0,
        awards: awardsData.length || 0,
      });
    } catch (err) { setError('Failed to load dashboard stats'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const cards = [
    { label: 'Blog Posts', value: stats.blogs, icon: FileText, color: 'bg-blue-500/10 text-blue-400', link: '/admin/blogs' },
    { label: 'Conferences', value: stats.conferences, icon: Globe, color: 'bg-teal-500/10 text-teal-400', link: '/admin/conferences' },
    { label: 'Community', value: stats.community, icon: Heart, color: 'bg-rose-500/10 text-rose-400', link: '/admin/community' },
    { label: 'FAQs', value: stats.faqs, icon: HelpCircle, color: 'bg-indigo-500/10 text-indigo-400', link: '/admin/faqs' },
    { label: 'Media & Press', value: stats.media, icon: Tv, color: 'bg-purple-500/10 text-purple-400', link: '/admin/media' },
    { label: 'Gallery Photos', value: stats.gallery, icon: Camera, color: 'bg-cyan-500/10 text-cyan-400', link: '/admin/gallery' },
    { label: 'Awards', value: stats.awards, icon: Trophy, color: 'bg-amber-500/10 text-amber-400', link: '/admin/awards' },
    { label: 'Messages', value: stats.messages, icon: MessageSquare, color: 'bg-orange-500/10 text-orange-400', link: '/admin/messages' },
    { label: 'Testimonials', value: stats.testimonials, icon: Star, color: 'bg-yellow-500/10 text-yellow-400', link: '/admin/testimonials' },
    { label: 'Subscribers', value: stats.subscribers, icon: Mail, color: 'bg-green-500/10 text-green-400', link: '/admin/newsletter' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-white mb-6">Dashboard</h1>
      {loading ? (
        <div className="text-slate-400">Loading...</div>
      ) : error ? (
        <div className="text-red-400 text-center py-8">{error}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {cards.map(card => (
            <Link key={card.label} to={card.link}>
              <Card className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer" data-testid={`dashboard-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm mb-1">{card.label}</p>
                      <p className="text-3xl font-bold text-white">{card.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${card.color}`}>
                      <card.icon className="w-6 h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
